import { bondTypes } from "../Component/OrderEntry/optionsHelper";

// Expand composite UI fields to actual data keys
const compositeExpansion = {
  couponRange: ["couponMin", "couponMax"],
  price: ["priceMin", "priceMax"],
  yield: ["yieldMin", "yieldMax"],
  maturityRanges: ["maturityMin", "maturityMax"],
};

// Map of synonyms: when one form is allowed/present we also accept/emit the other
const synonymsMap = {
  // price naming variants
  priceMin: ["minPrice"],
  priceMax: ["maxPrice"],
  minPrice: ["priceMin"],
  maxPrice: ["priceMax"],

  // coupon naming variants
  couponMin: ["minCoupon"],
  couponMax: ["maxCoupon"],
  minCoupon: ["couponMin"],
  maxCoupon: ["couponMax"],

  // yield naming variants (UI vs API)
  yieldMin: ["minYtm"],
  yieldMax: ["maxYtm"],
  minYtm: ["yieldMin"],
  maxYtm: ["yieldMax"],

  // maturity naming variants
  maturityMin: ["minMaturity"],
  maturityMax: ["maxMaturity"],
  minMaturity: ["maturityMin"],
  maxMaturity: ["maturityMax"],

  // callable/call aliases
  callable: ["call"],
  call: ["callable"],

  // rating aliases
  minimumRating: ["minRatingSP"],
  minRatingSP: ["minimumRating"],
  minimumRatingMoody: ["minRatingMoody"],
  minRatingMoody: ["minimumRatingMoody"],
};

/**
 * Build the allowed keys list for a given bondType (UI names + data keys)
 * @param {string} bondType - e.g. 'Treasury' | 'Corporates' | 'Municipals'
 * @returns {Set<string>}
 */
const getAllowedKeysSet = (bondType) => {
  const def = bondTypes[bondType] || {};
  const fields = def.fields || [];
  const allowed = new Set();

  fields.forEach(f => {
    if (compositeExpansion[f]) {
      compositeExpansion[f].forEach(k => allowed.add(k));
    } else {
      allowed.add(f);
    }
  });

  // Rating variants
  if (allowed.has("minimumRating")) {
    allowed.add("minimumRating");
    allowed.add("minRatingSP");
    allowed.add("maxRatingSP");
  }
  if (allowed.has("minimumRatingMoody")) {
    allowed.add("minimumRatingMoody");
    allowed.add("minRatingMoody");
    allowed.add("maxRatingMoody");
  }

  // Duration stored sometimes as 'duration' plus comparison helpers
  if (allowed.has("duration")) {
    allowed.add("durationComparison"); // local UI helper field
  }
  //bondType and side is always allowed
  allowed.add("bondType");
  allowed.add("type"); // API field
  allowed.add("side");
  allowed.add("action");

  // Ensure synonyms are represented in allowed set only if their counterpart exists.
  // This keeps the allowed list tight to the bondType while accepting alternate labels.
  Object.entries(synonymsMap).forEach(([key, syns]) => {
    if (allowed.has(key)) {
      syns.forEach(s => allowed.add(s));
    }
  });

  return allowed;
};

/**
 * Filters an arbitrary object (characteristics, filters, etc.) keeping only fields
 * supported for the supplied bondType.
 * It is agnostic of whether keys are in "raw" or transformed (API) naming,
 * as long as they map to allowed semantic fields.
 * @param {string} bondType
 * @param {object} obj
 * @returns {object} filtered shallow copy
 */
export const filterObjectByBondType = (bondType, obj = {}) => {
  if (!obj || typeof obj !== "object") return {};
  const allowed = getAllowedKeysSet(bondType);
  const result = {};

  Object.entries(obj).forEach(([k, v]) => {
    if (allowed.has(k)) {
      result[k] = v;

      // If this key has synonyms, also add them so cleaned filters contain both naming styles
      const syns = synonymsMap[k];
      if (Array.isArray(syns)) {
        syns.forEach(syn => {
          // Only add synonym if it's allowed (was enabled for this bondType)
          if (allowed.has(syn)) {
            result[syn] = v;
          }
        });
      }
    }
  });

  return result;
};
